/**
 * Captura del fotograma — lógica PURA, sin React ni Redux.
 *
 * Tres responsabilidades, todas comprobables sin cámara real:
 *
 *  1. Recortar el frame cuando el zoom es DIGITAL, con exactamente el mismo
 *     factor que el `transform: scale()` del visor. Si los dos números se
 *     separan, el encuadre vuelve a mentir — que es el defecto que esta ficha
 *     viene a cerrar.
 *  2. Producir un `Blob` (nunca `toDataURL`, que materializa un base64
 *     enorme en memoria) y hacerlo FUERA del hilo principal cuando se puede:
 *     ver `frameEncoder.ts`.
 *  3. Garantizar nombres ÚNICOS dentro de una misma ráfaga (ver
 *     `makeUniqueName`).
 */

import { UPLOAD_MAX_DIMENSION } from '../../../../utils/imageCompression';
import { canEncodeOffThread, encodeFrame } from './frameEncoder';

/**
 * Calidad JPEG de la captura.
 *
 * Antes era `1.0`, que produce ficheros enormes que `compressForUpload`
 * recodificaba acto seguido — dos pérdidas encadenadas. A 0,92 y ya dentro del
 * tope de dimensión, la foto suele cumplir `yaCumpleElTope` y la compresión la
 * deja INTACTA: menos CPU en la tablet y mejor calidad final que antes.
 */
export const CAPTURE_QUALITY = 0.92;

/** Tope de zoom del motor digital (el nativo usa el rango del dispositivo). */
export const MAX_DIGITAL_ZOOM = 4;

export interface CropRect {
  sx: number;
  sy: number;
  sw: number;
  sh: number;
}

/**
 * Rectángulo del frame que hay que dibujar para un zoom digital dado.
 *
 * El visor aplica `transform: scale(z)` con el origen en el centro, así que lo
 * que el operario ve es el rectángulo central de tamaño `1/z`. Aquí se
 * reproduce ese mismo recorte sobre el frame original.
 */
export const computeCropRect = (
  width: number,
  height: number,
  zoom: number
): CropRect => {
  const z = Number.isFinite(zoom) && zoom > 1 ? zoom : 1;
  const sw = width / z;
  const sh = height / z;
  return {
    sx: (width - sw) / 2,
    sy: (height - sh) / 2,
    sw,
    sh,
  };
};

/**
 * Punto de enfoque normalizado (0..1) sobre el CONTENIDO del vídeo.
 *
 * El visor usa `object-fit: contain`, así que dentro de la caja hay bandas
 * (arriba/abajo o a los lados) que no son imagen. Tocar una banda no es tocar
 * la escena, y mandarle al sensor un punto de ahí sería mentirle.
 *
 * Devuelve `null` si el toque cae fuera de la imagen.
 */
export const toVideoPoint = (
  clientX: number,
  clientY: number,
  rect: { left: number; top: number; width: number; height: number },
  videoWidth: number,
  videoHeight: number,
  zoom = 1
): { x: number; y: number } | null => {
  if (!videoWidth || !videoHeight || !rect.width || !rect.height) return null;

  const scale = Math.min(rect.width / videoWidth, rect.height / videoHeight);
  const shownWidth = videoWidth * scale;
  const shownHeight = videoHeight * scale;
  const offsetX = (rect.width - shownWidth) / 2;
  const offsetY = (rect.height - shownHeight) / 2;

  const x = (clientX - rect.left - offsetX) / shownWidth;
  const y = (clientY - rect.top - offsetY) / shownHeight;

  if (x < 0 || x > 1 || y < 0 || y > 1) return null;

  // Con zoom digital solo se ve la parte central `1/z` de la escena, así que
  // el punto visible hay que trasladarlo a coordenadas del frame completo.
  const z = zoom > 1 ? zoom : 1;
  return {
    x: 0.5 + (x - 0.5) / z,
    y: 0.5 + (y - 0.5) / z,
  };
};

/**
 * ¿Hay ya un fotograma con dimensiones reales?
 *
 * `videoWidth`/`videoHeight` valen 0 hasta que llegan los metadatos. Disparar
 * antes deja un canvas de 0×0 y una foto vacía — alcanzable de verdad ahora que
 * la ráfaga permite disparar sin esperar a nada.
 */
export const hasUsableFrame = (video: HTMLVideoElement | null): boolean =>
  !!video && video.videoWidth > 0 && video.videoHeight > 0;

/**
 * Respaldo: dibuja el recorte en el canvas y lo codifica con `toBlob`, en el
 * hilo principal. `toBlob` toma la instantánea del canvas al llamarlo, así que
 * el fotograma queda congelado aunque la codificación tarde.
 */
const encodeOnCanvas = (
  video: HTMLVideoElement,
  canvas: HTMLCanvasElement,
  { sx, sy, sw, sh }: CropRect
): Promise<Blob> => {
  canvas.width = Math.round(sw);
  canvas.height = Math.round(sh);

  const ctx = canvas.getContext('2d');
  if (!ctx) return Promise.reject(new Error('canvas 2d context no disponible'));

  ctx.drawImage(video, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);

  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (blob) => {
        if (blob) resolve(blob);
        else reject(new Error('canvas.toBlob devolvió null'));
      },
      'image/jpeg',
      CAPTURE_QUALITY
    );
  });
};

/**
 * Congela el fotograma de ESTE instante (con el recorte del zoom digital) y
 * devuelve su codificación ya en marcha.
 *
 * Coger y codificar van separados a propósito: coger cuesta ~10–40 ms y
 * codificar ~0,2 s en el worker. Es lo que permite la ráfaga de las apps de
 * cámara — fotogramas a ritmo fijo mientras se mantiene pulsado, procesados
 * detrás de uno en uno.
 *
 * Si el worker no está disponible o falla, se codifica en el canvas de
 * siempre: más lento con la pantalla ocupada, pero funciona en cualquier
 * navegador.
 */
export const grabFrame = async (
  video: HTMLVideoElement,
  canvas: HTMLCanvasElement,
  digitalZoom = 1
): Promise<{ blob: Promise<Blob> }> => {
  const crop = computeCropRect(video.videoWidth, video.videoHeight, digitalZoom);
  if (canEncodeOffThread()) {
    try {
      const bitmap = await createImageBitmap(video, crop.sx, crop.sy, crop.sw, crop.sh);
      return {
        blob: encodeFrame(bitmap, CAPTURE_QUALITY).catch((error) => {
          console.warn('[Camera] el worker no pudo codificar; se usa el canvas', error);
          return encodeOnCanvas(video, canvas, crop);
        }),
      };
    } catch (error) {
      console.warn('[Camera] createImageBitmap falló; se usa el canvas', error);
    }
  }
  return { blob: encodeOnCanvas(video, canvas, crop) };
};

/** Coge el fotograma actual y espera a tenerlo codificado. */
export const captureFrameToBlob = async (
  video: HTMLVideoElement,
  canvas: HTMLCanvasElement,
  digitalZoom = 1
): Promise<Blob> => (await grabFrame(video, canvas, digitalZoom)).blob;

/**
 * Restricciones de vídeo con las que se pide la cámara.
 *
 * Antes se abría el stream y acto seguido se le aplicaba la resolución MÁXIMA
 * del sensor. Eso calienta la tablet y no aporta un píxel útil: la foto se
 * recorta a `UPLOAD_MAX_DIMENSION` antes de subirla. Se pide directamente ese
 * tamaño como `ideal` — preferencia, no requisito, así que ningún dispositivo
 * puede fallar con `OverconstrainedError` por esto.
 */
/**
 * 🔴 Solo el ANCHO, nunca los dos lados.
 *
 * Pedir `width: {ideal: 2560}` **y** `height: {ideal: 2560}` no significa «que
 * ningún lado pase de 2560»: el navegador lo lee como «quiero 2560×2560» y
 * elige el modo más parecido, que es **CUADRADO**. Medido en vivo: una webcam
 * que da 16:9 devolvía `1552×1552` y el visor salía cuadrado, tirando medio
 * encuadre del sensor.
 *
 * Con el ancho a solas, el alto queda libre y la cámara conserva su proporción
 * nativa. Y va como `ideal`, que es una preferencia: ningún dispositivo puede
 * fallar con `OverconstrainedError` por esto.
 */
export const RESOLUTION_CONSTRAINTS: MediaTrackConstraints = {
  /**
   * 🔴 Tiene que haber un OBJETIVO de tamaño. Sin él —solo topes y proporción—
   * el navegador se queda con su resolución por defecto: medido, **640×480**.
   * Con el ancho como `ideal` sale un fotograma grande de verdad (1760×1328 en
   * la webcam de pruebas, 1920×1080 sin pedir proporción).
   *
   * Y el objetivo va SOLO en el ancho: poner `ideal` también en el alto es lo
   * que hace que el navegador entienda «N×N» y devuelva un vídeo CUADRADO.
   */
  width: { ideal: UPLOAD_MAX_DIMENSION },
  /**
   * 4:3 es la proporción NATIVA del sensor en casi todas las cámaras de móvil
   * y tablet; el 16:9 que sale por defecto es un **recorte** de ella, o sea
   * menos imagen. Para documentar una incidencia interesa lo contrario.
   *
   * Va como `ideal`, así que no puede fallar, y funciona en las dos
   * orientaciones: el navegador elige el modo cuyo valor esté más cerca. En
   * vertical los candidatos son 0,5625 (16:9) y 0,75 (4:3), y 0,75 está más
   * cerca de 1,333 → sale 4:3. En apaisado acierta de lleno.
   *
   * Efecto secundario buscado: 4:3 es además menos alargado, así que el visor
   * llena mucho mejor una pantalla de tablet.
   */
  aspectRatio: { ideal: 4 / 3 },
};

export const videoConstraintsFor = (
  deviceId?: string
): MediaTrackConstraints => ({
  ...(deviceId ? { deviceId: { ideal: deviceId } } : {}),
  ...RESOLUTION_CONSTRAINTS,
});

/**
 * Proporción del visor mientras aún no se conoce el fotograma.
 *
 * Se recuerda la de la última vez para que el modal ABRA ya con su tamaño
 * definitivo: sin esto, la caja nace con una proporción supuesta y da un salto
 * visible en cuanto llegan los metadatos. 16:9 como valor de partida porque es
 * lo que dan casi todas las webcams y las traseras de tablet.
 */
const RATIO_KEY = 'rois_camera_ratio';
export const DEFAULT_RATIO = 16 / 9;

export const readStoredRatio = (): number => {
  try {
    const raw = localStorage.getItem(RATIO_KEY);
    const n = raw ? Number(raw) : Number.NaN;
    // Se acota a proporciones sensatas: un valor corrupto no puede dejar el
    // visor convertido en una raya.
    return Number.isFinite(n) && n >= 0.5 && n <= 3 ? n : DEFAULT_RATIO;
  } catch {
    return DEFAULT_RATIO;
  }
};

export const storeRatio = (ratio: number): void => {
  try {
    if (Number.isFinite(ratio) && ratio > 0) {
      localStorage.setItem(RATIO_KEY, String(ratio));
    }
  } catch {
    // Modo privado o almacenamiento lleno: es una preferencia cosmética.
  }
};

// Los nombres únicos viven en `utils/uniqueFileName.ts` (módulo PURO, sin la
// cámara detrás): también los usan los croquis de los asistentes
// (`sketchFile.ts`). Se reexportan para no tocar los imports existentes.
export { makeUniqueName, makeUniqueFileName } from '../../../../utils/uniqueFileName';
